import { path } from "../../api";
import * as actions from "../../utils/actions";
import { restClient } from "../../utils/restClient";

const state = {
  loadingCount: 0,
  terminals: [],
  summary: null,
  transferHistory: [],
  bulkSales: null,
  pagination: {
    page: 1,
    more: false,
    total: 0,
  },
  transferPagination: {
    page: 1,
    more: false,
    total: 0,
    limit: 10,
  },
};

const mutations = {
  [actions.GET_SUB_ACCOUNTS](state, payload) {
    state.terminals = payload?.data?.data || [];
    state.pagination = {
      page: payload?.pagination?.current_page || 1,
      more: payload?.pagination?.more || false,
      total: payload?.recordsFiltered || 0,
      limit: payload?.pagination?.limit || 10,
    };
  },
  [actions.GET_SUB_ACCOUNTS_SUMMARY](state, payload) {
    state.summary = payload;
  },
  [actions.SET_SUB_ACCOUNTS_LOADING](state, payload) {
    if (payload) {
      state.loadingCount++;
    } else {
      state.loadingCount = Math.max(0, state.loadingCount - 1);
    }
  },
  [actions.GET_SUB_ACCOUNT_TRANSFER_HISTORY](state, payload) {
    state.transferHistory = payload?.data || [];
    state.transferPagination = {
      page: payload?.pagination?.page || 1,
      more: payload?.pagination?.more || false,
      total: payload?.recordsFiltered || 0,
      limit: payload?.pagination?.limit || 10,
    };
  },
  [actions.GET_BULK_SUB_ACCOUNT_SALES](state, payload) {
    state.bulkSales = payload;
  },
};

const actionsDefinition = {
  async fetchAll({ commit }, payload = {}) {
    try {
      commit(actions.SET_SUB_ACCOUNTS_LOADING, true);
      const response = await restClient(
        path.client.subAccounts.base,
        "GET",
        null,
        payload
      );
      commit(actions.GET_SUB_ACCOUNTS, response);
      return response;
    } catch (error) {
      commit(actions.GET_SUB_ACCOUNTS, []);
      throw error;
    } finally {
      commit(actions.SET_SUB_ACCOUNTS_LOADING, false);
    }
  },
  async fetchSummary({ commit }) {
    try {
      commit(actions.SET_SUB_ACCOUNTS_LOADING, true);
      const response = await restClient(path.client.subAccounts.summary, "GET");
      commit(actions.GET_SUB_ACCOUNTS_SUMMARY, response.data);
      return response;
    } finally {
      commit(actions.SET_SUB_ACCOUNTS_LOADING, false);
    }
  },
  async create({ commit }, payload = {}) {
    try {
      commit(actions.SET_SUB_ACCOUNTS_LOADING, true);
      const response = await restClient(
        path.client.subAccounts.base,
        "POST",
        payload
      );
      return response;
    } finally {
      commit(actions.SET_SUB_ACCOUNTS_LOADING, false);
    }
  },
  async update({ commit }, { id, ...payload }) {
    try {
      commit(actions.SET_SUB_ACCOUNTS_LOADING, true);
      const response = await restClient(
        path.client.subAccounts.single(id),
        "PUT",
        payload
      );
      return response;
    } finally {
      commit(actions.SET_SUB_ACCOUNTS_LOADING, false);
    }
  },
  async toggleStatus({ commit }, id) {
    try {
      commit(actions.SET_SUB_ACCOUNTS_LOADING, true);
      const response = await restClient(
        path.client.subAccounts.toggleStatus(id),
        "PATCH"
      );
      return response;
    } finally {
      commit(actions.SET_SUB_ACCOUNTS_LOADING, false);
    }
  },
  async transferBalance({ commit }, { id, ...payload }) {
    try {
      commit(actions.SET_SUB_ACCOUNTS_LOADING, true);
      const response = await restClient(
        path.client.subAccounts.transfer(id),
        "POST",
        payload
      );
      return response;
    } finally {
      commit(actions.SET_SUB_ACCOUNTS_LOADING, false);
    }
  },
  async withdrawBalance({ commit }, { id, ...payload }) {
    try {
      commit(actions.SET_SUB_ACCOUNTS_LOADING, true);
      const response = await restClient(
        path.client.subAccounts.withdraw(id),
        "POST",
        payload
      );
      return response;
    } finally {
      commit(actions.SET_SUB_ACCOUNTS_LOADING, false);
    }
  },
  async fetchSalesReport({ commit }, { id, ...params }) {
    try {
      commit(actions.SET_SUB_ACCOUNTS_LOADING, true);
      const response = await restClient(
        path.client.subAccounts.salesReport(id),
        "GET",
        null,
        params
      );
      return response;
    } finally {
      commit(actions.SET_SUB_ACCOUNTS_LOADING, false);
    }
  },
  async fetchTransferHistory({ commit }, payload = {}) {
    try {
      commit(actions.SET_SUB_ACCOUNTS_LOADING, true);
      const response = await restClient(
        path.client.subAccounts.transfers,
        "GET",
        null,
        payload
      );
      commit(actions.GET_SUB_ACCOUNT_TRANSFER_HISTORY, response);
      return response;
    } finally {
      commit(actions.SET_SUB_ACCOUNTS_LOADING, false);
    }
  },
  async fetchBulkSales({ commit }, params = {}) {
    try {
      commit(actions.SET_SUB_ACCOUNTS_LOADING, true);
      const response = await restClient(
        path.client.subAccounts.bulkSales,
        "GET",
        null,
        params
      );
      commit(actions.GET_BULK_SUB_ACCOUNT_SALES, response.data);
      return response;
    } finally {
      commit(actions.SET_SUB_ACCOUNTS_LOADING, false);
    }
  },
  async updateBulkLimits({ commit }, payload) {
    try {
      commit(actions.SET_SUB_ACCOUNTS_LOADING, true);
      const response = await restClient(
        path.client.subAccounts.bulkLimits,
        "POST",
        payload
      );
      return response;
    } finally {
      commit(actions.SET_SUB_ACCOUNTS_LOADING, false);
    }
  },
};

const getters = {
  getTerminals: (state) => state.terminals,
  getSummary: (state) => state.summary,
  isLoading: (state) => state.loadingCount > 0,
  getPagination: (state) => state.pagination,
  getTransferHistory: (state) => state.transferHistory,
  getTransferPagination: (state) => state.transferPagination,
  getBulkSales: (state) => state.bulkSales,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions: actionsDefinition,
  getters,
};
